/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import _ from "underscore";
import validate from "../util/validate.js";
import fieldManager from "../util/fields.js";
import util from "../util/util.js";
import * as utmGrid from "../map/utm-grid.js";
import MediaUploaderViewModel from "../ui/shared/media-uploader.js";

var program = {
	code: "segbc",
	name: "South-eastern Glossy Black-Cockatoo",
	description: `
		<p>The SE Glossy Black-Cockatoo surveys are a citizen science initiative that aims to help us improve our understanding about the Southeastern sub-species of Glossy Black-Cockatoos across Queensland, New South Wales, the Australian Capital Territory, and Victoria. If you find Glossy Black-cockatoos in these areas, or evidence that they have been feeding on she-oaks, you can use this survey to enter the details of your observation. This data will go contribute to the protection of Glossy Black-Cockatoos, their habitat, and their recovery.</p>
		<p><a target='_blank' href='https://bit.ly/glossycount'>Find out more…</a></p>
	`,
	canFilterBy: function(user) {
		return user.is_administrator || _.any(user.permissions, function(p) {
			return p.type == "program" && p.program == "segbc";
		});
	},
	load: function() {
		return Promise.all([birdata.lookup()]).then(([lookups]) => {
			program.options = lookups;
			return true;
		});
	},
	fields: function(survey) {
		var fields = [
			{
				name: "siteArrivalTime",
				type: "local-time",
				label: "When did you arrive at your site/grid? (24 hr)",
				required: true,
				validate: function(field) {
					let val = field.value();
					if(!val) {
						return field.label + " is required";
					}

					let parsedValue = util

					if(!parsedValue) {
						return field.label + " is not a valid 24-hour time";
					}

					let parsedStartTime = util.parseLocalTime(survey.startTime());

					if(parsedStartTime && parsedValue.hhmm > parsedStartTime.hhmm) {
						return field.label + " must not be after time of observation";
					}
				}
			},
			{
				name: "observers",
				type: "text",
				label: "Additional observers"
			},
			{
				name: "weatherConditions",
				label: "Weather conditions at time of observation",
				type: "options",
				options: program.options.weather_condition,
				multiselect: true,
				style: "checkbox",
				validate: function(field) {
					let val = field.value();
					if(!val || val.length == 0) {
						return field.label + " is required";
					}
				},
				required: true,
				defaultValue: []
			},
			{
				name: "otherWeatherCondition",
				type: "text",
				validate: "required",
				placeholder: "Please specify",
				hidden: () => !(fields.weatherConditions.field.to_json() || []).includes("other")
			},
			{
				name: "experience",
				type: "options",
				options: program.options.segbc_experience,
				label: "Observer experience in surveying Glossy Black-Cockatoo",
				validate: "required"
			},
			{
				name: "waterSource",
				type: "options",
				options: program.options.segbc_water_source,
				label: "Did you see Glossies drinking here, and from what source?",
				validate: "required"
			},
			{
				name: "waterSourceDescription",
				type: "text",
				validate: "required",
				label: "Please specify type/describe E.g. Small dam",
				hidden: () => fields.waterSource.field.to_json() == null || fields.waterSource.field.to_json() == "none"
			},
			{
				name: "feedTreePhotos",
				label: "Photo(s) of she-oaks, chewings, or water source",
				type: "custom",
				isArrayType: true,
				key: 'feed_tree_photo_uuids',
				to_json: function(value) {
					return value().map(x => x.uuid()).filter(x => x);
				},
				from_json: function(json) {
					if(json) {
						return json.map(uuid => MediaUploaderViewModel.createMediaModel(null, uuid));
					} else {
						return [];
					}
				},
				formattedValue: function(val) {
					return util.formatQuantity(val.length, "item");
				}
			},
			{
				name: "treeObserved",
				type: "boolean",
				style: "radio",
				label: "Have you found any she-oaks?",
				validate: "required",
				defaultValue: null
			},
			{
				name: "treeCount",
				type: "options",
				options: program.options.segbc_tree_count,
				label: "From where you are standing, roughly how many she-oaks are around you?",
				validate: "required",
				hidden: () => !fields.treeObserved(),
			},
			{
				name: "treesHaveCones",
				type: "boolean",
				style: "radio",
				label: "Do any of the she-oaks have cones?",
				validate: "required",
				defaultValue: null,
				hidden: () => !fields.treeObserved(),
			},
			{
				name: "treesHaveChewingsOrFeeding",
				type: "boolean",
				style: "radio",
				label: "Do any of these she-oaks with cones have chewings underneath them or Glossies feeding in them?",
				validate: "required",
				defaultValue: null,
				hidden: () => fields.treesHaveCones.field.hidden() || !fields.treesHaveCones(),
			},
			{
				name: "feedTreeCount",
				type: "options",
				options: program.options.segbc_feed_tree_count,
				label: "How many she-oaks have chewings underneath them or Glossies feeding in them?",
				validate: "required",
				validate: function(field) {
					let val = field.value();
					if(!val) {
						return field.label + " is required";
					}
					let treeCount = fields.treeCount();
					if(treeCount) {
						let valIndex = program.options.segbc_feed_tree_count.indexOf(val);
						let treeCountIndex = program.options.segbc_tree_count.indexOf(treeCount);
						if(valIndex > treeCountIndex) {
							return "Number of of she-oaks with chewings or Glossies feeding in them cannot be more than the number of she-oaks observed";
						}
					}
				},
				hidden: () => fields.treesHaveChewingsOrFeeding.field.hidden() || !fields.treesHaveChewingsOrFeeding(),
			},
			{
				name: "feedTrees",
				type: "compound",
				minCount: 1,
				maxCount: 99,
				hidden: () => fields.treesHaveChewingsOrFeeding.field.hidden() || !fields.treesHaveChewingsOrFeeding(),
				fields: [{
					name: "treeSpecies",
					label: "Tree species",
					type: "options",
					options: program.options.segbc_tree_species,
					required: true,
					validate:  function(field) {
						var code = field.to_json();

						if(!code) {
							return field.label + " is required";
						}

						var feedTrees = fields.feedTrees()
						var i, otherTreeSpecies;
						for(i = 0; i < feedTrees.length; i++) {
							otherTreeSpecies = feedTrees[i].treeSpecies;
							if(otherTreeSpecies.field === field) {
								break;
							}
							if(otherTreeSpecies.field.to_json() === code) {
								return otherTreeSpecies().name + ' has already been entered. Each tree species may only be entered once';
							}
						}
					},
					help: `
					<div class="col">
					<h4>QLD</h4>
					<ul>
					<li>Belah</li>
					<li>Black She-oak</li>
					<li>Buloke</li>
					<li>Coastal She-oak</li>
					<li>Forest She-oak</li>
					<li>Stringybark She-oak</li>
					</ul>

					<h4>VIC</h4>
					<ul>
					<li>Black She-oak</li>
					<li>Drooping She-oak</li>
					</ul>
					</div>
					<div class="col">
					<h4>NSW</h4>
					<ul>
					<li>Belah</li>
					<li>Black She-oak</li>
					<li>Broombush She-oak</li>
					<li>Buloke</li>
					<li>Coastal She-oak</li>
					<li>Drooping She-oak</li>
					<li>Forest She-oak</li>
					<li>Mallee She-oak</li>
					<li>Scrub She-oak</li>
					<li>Stringybark She-oak</li>
					</ul>
					</div>
					<div class="col">
					<h4>ACT</h4>
					<ul>
					<li>Belah</li>
					<li>Black She-oak</li>
					<li>Buloke</li>
					<li>Coastal She-oak</li>
					<li>Drooping She-oak</li>
					<li>Forest She-oak</li>
					</ul>
					</div>`
				},
				{
					name: "chewings",
					label: "Chewing colours",
					type: "options",
					multiselect: true,
					style: "checkbox",
					options: program.options.segbc_chewings,
					help: '<img src="https://birdata.birdlife.org.au/app/images/segbc_chewings.jpg" style="max-width: 100%;">',
					optionImages: {
						"brown_to_dark_orange": "lookup/segbc_chewings__brown_to_dark_orange.jpg",
						"grey": "lookup/segbc_chewings__grey.jpg",
						"red_brown_or_light_orange": "lookup/segbc_chewings__red_brown_or_light_orange.jpg",
						"whitish_green": "lookup/segbc_chewings__whitish_green.jpg"
					}
				}]
			},
			{
				name: "totalCount",
				type: "number",
				maxLength: 4,
				max: 9999,
				label: "Total number of Glossy Black-Cockatoos observed",
				validate: function(field) {
					var n = field.value();

					if(!n && n !== 0) {
						return field.label + " is required";
					}

					if(!util.isInt(n)) {
						return field.label + " must be a number";
					}
					var total = 0;

					_.each(survey.sightings(), function(s) {
						if(util.isInt(s.count())) {
							total += (+s.count());
						}
					});

					if(n != total) {
						return field.label + " must equal total count entered in sightings section (" + total + ")";
					}
				},
				required: true
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		if(survey) {
			ko.computed(function() {
				var arrivalTime = util.parseLocalTime(fields.siteArrivalTime());
				var observationTime = util.parseLocalTime(survey.startTime());

				if(observationTime && arrivalTime) {
					let t1 = arrivalTime.hours * 60 + arrivalTime.minutes;
					let t2 = observationTime.hours * 60 + observationTime.minutes;
					let dt = t2 - t1;
					if(dt >= 0) {
						if(dt == 0) {
							dt = 1;
						}
						survey.durationHours(Math.floor(dt / 60));
						survey.durationMinutes(dt % 60);
					}
				}
			});
		}

		return fields;
	},
	sightingFields: function() {
		var fields = [
			{
				name: "sex",
				label: "Sex",
				type: "options",
				options: program.options.segbc_sex,
				validate: "required",
				help: `<img src="https://birdata.birdlife.org.au/app/images/segbc_male_female.jpg" style="max-width: 100%;">
				<p>An example of a Glossy Black-Cockatoo female (left) and male (right).</p>
				<p>The female here has yellow facial patches, while the male has none. You can also look at their tail feathers to help establish the bird's sex.</p>
				<p>Photo by Peter Murrell.</p>`
			},
			{
				name: "age",
				label: "Age",
				type: "options",
				options: program.options.segbc_age,
				validate: "required"
			},
			{
				name: "activity",
				label: "Main activity",
				type: "options",
				options: program.options.segbc_activity,
				validate: "required"
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	programData: function(survey) {
		var fields = program.fields(survey);

		var speciesList = ko.observable([]);
		birdata.speciesList().then(function(spp) { // Note this causes warning about promise not being returned
			speciesList(spp.filter(function(sp) { return sp.id == 265; }));
		});

		return {
			fields: fields,
			validations: ko.pureComputed(function() {
				// This is dynamic so that we can validate nested plant fields
				return _.flatten(_.values(fields).map(f => f.field.withDescendants(true))).map(f => f.value);
			}),
			speciesList: speciesList
		};
	},
	mapGrid: {
		type: "mga",
		size: 1000
	},
	multipleSightingsPerSpecies: true,
	surveyPointFilter: (sp) => sp.program == 'segbc',
	disableSurveyPointDetailEditing: true,
	allSpeciesRecordedEnabled: (survey) => false,
	handleSurveyPointSelection: function(surveyPoint, survey) {
		if(!ko.isObservable(surveyPoint.name)) {
			surveyPoint.name = ko.observable(surveyPoint.name);
		}
		var cellID = utmGrid.gridCellIDFromLatLon(surveyPoint.lat, surveyPoint.lon, 1000);
		surveyPoint.name(cellID);

		if(!survey.existing()) {
			// Look for most recent survey in grid cell and prepopulate fields accordingly
			birdata.mySurveys({
				program: 'segbc',
				xmin: surveyPoint.lon - 0.02,
				xmax: surveyPoint.lon + 0.02,
				ymin: surveyPoint.lat - 0.02,
				ymax: surveyPoint.lat + 0.02,
				sort: "-start_date",
				limit: 1
			}).then(function(surveys) {
				if(surveys.length) {
					var surveyJSON = surveys[0];
					var pd = survey.programData().segbc;

					function copyField(name, key) {
						let jsonValue = surveyJSON.segbc[key];
						if(jsonValue !== undefined) {
							pd.fields[name].field.from_json(jsonValue);
						}
					}

					if(pd) {
						copyField('experience', 'experience');
						var today = (new Date()).toISOString().substr(0,10);
						if(surveyJSON.start_date == today) {
							copyField('weatherConditions', 'weather_conditions');
							copyField('otherWeatherCondition', 'other_weather_condition');
							copyField('siteArrivalTime', 'site_arrival_time');
							copyField('observers', 'observers');
						}
					}
				}
			});
		}
	},
	visibleSharedSiteLayers: () => [],
	filterAreaLayers: (layers) => [],
	defaultAreaLayer: (layers) => null,
	multipleDaysEnabled: function() { return false; },
	requireSightingCount: true,
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().segbc.fields;
		json.segbc = {};

		_.each(fields, function(f) {
			f.field.save(json.segbc);
		});

		if(json.survey_point) {
			localStorage.segbc_last_lat = json.survey_point.lat;
			localStorage.segbc_last_lon = json.survey_point.lon;
		}

		if(!json.segbc.feed_trees) {
			json.segbc.feed_trees = [];
		}

		delete json.segbc.tree_observed;
		delete json.segbc.total_count;
	},
	updateMap: function(map, survey, areasPromise, locationViewModel) {
		// Zoom to SOP Area
		var lat = +localStorage.segbc_last_lat;
		var lon = +localStorage.segbc_last_lon;
		if(lat && lon) {
			map.setCenter({ lat: lat, lng: lon });
			map.setZoom(16);
		} else {
			// Roughly zoom into range of GBC
			map.setCenter({ lat: -28.5072141, lng: 150.4102837 });
			map.setZoom(6);
		}
	},
	hideWaterLevel: true,
	hideSurveyType: true,
	surveyTypeOptions: [],
	multipleDaysEnabled: function() { return false; },
	durationEnabled: function() { return false; },
	afterSelect: function(survey) {
		// Set survey type after program selected
		survey.type({
			code: 'grid_survey',
			name: 'Grid survey'
		});
        survey.multipleDays(false);
		survey.allSpeciesRecorded(false);
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.segbc;

		if(!data) return;

		_.each(fields, function(f) {
			f.field.load(data);
		});

		var totalCount = (json.sightings || []).reduce((sum, x) => sum += (x.count || 0), 0);
		fields.totalCount(totalCount);
		fields.treeObserved(!!fields.treeCount());
	},
	recordAnotherSurveyURL: function() {
		return "record-survey?program=segbc&rand=" + Math.random() + "#tab=location";
	},
	forcePrivate: true,
	startTimeLabel: function(survey) {
		return "Time of observation";
	}
};

export default program;
